import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useProjectCsvMappingQuery,
  useUpdateProjectCsvMapping,
} from '@/features/projects/hooks/useProjectCsvMapping';
import { projectCsvMappingAnchorId } from '@/features/projects/utils/projectSettingsRoute';
import { getToastMessage } from '@/shared/copy/toasts';
import { useMutationErrorSnackbar } from '@/shared/hooks';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { Box, DialogBody, DialogSection, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { CursorTooltip, ErrorOutline, HelpOutline } from '@/ui/primitives';
import { CheckCircleOutlineIcon } from '@/ui/primitives/icons';
import {
  type CsvHeaderAliases,
  type CsvStatusAliases,
  defaultCsvHeaderAliases,
  defaultCsvStatusAliases,
} from '@flowqa/shared';
import { parseValidationRows } from './csvValidationFileParser';
import { ProjectCsvMappingActions } from './ProjectCsvMappingActions';
import { ProjectCsvMappingFieldRow } from './ProjectCsvMappingFieldRow';
import { ProjectCsvMappingPanelGroup } from './ProjectCsvMappingPanelGroup';
import {
  buildHeaderMapping,
  buildStatusMapping,
  fieldOptions,
  validationIconCursorTooltipSlotProps,
  type ValidationStatus,
} from './projectCsvMappingSectionShared';
import { ProjectCsvUploadValidationPanel } from './ProjectCsvUploadValidationPanel';
import { ProjectSettingsAccordion } from './ProjectSettingsAccordion';
import { projectSettingsCsvMappingPanelDividerSx } from './projectSettingsSurfaceSx';
import { buildValidationPreviewData } from './validationDataChecks';

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };
type ProjectCsvMappingSectionProps = {
  projectId: string | null;
  notify: (input: NotifyInput) => void;
  container?: 'accordion' | 'embedded' | 'panel';
};

type StatusFieldDefinition = { key: keyof CsvStatusAliases; label: string };

const statusFieldDefinitions: readonly StatusFieldDefinition[] = [
  { key: 'passed', label: 'Passed' },
  { key: 'failed', label: 'Failed' },
  { key: 'errored', label: 'Errored' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'untested', label: 'Untested' },
];

const mappingPanelsGap = 'sm' as const;

export const ProjectCsvMappingSection = ({
  projectId,
  notify,
  container = 'accordion',
}: ProjectCsvMappingSectionProps): ReactElement => {
  const location = useLocation();
  const accordionRef = useRef<HTMLDivElement | null>(null);
  const isHashTargeted = location.hash === `#${projectCsvMappingAnchorId}`;
  const defaultHeaderMapping = useMemo(() => buildHeaderMapping(defaultCsvHeaderAliases), []);
  const defaultStatusMapping = useMemo(() => buildStatusMapping(defaultCsvStatusAliases), []);
  const [mapping, setMapping] = useState<Record<string, string>>(defaultHeaderMapping);
  const [draftMapping, setDraftMapping] = useState<Record<string, string>>(defaultHeaderMapping);
  const [statusAliases, setStatusAliases] = useState<Record<string, string>>(defaultStatusMapping);
  const [draftStatusAliases, setDraftStatusAliases] =
    useState<Record<string, string>>(defaultStatusMapping);
  const [headerValidation, setHeaderValidation] = useState<Record<string, ValidationStatus>>(() =>
    fieldOptions.reduce<Record<string, ValidationStatus>>((acc, field) => {
      acc[field.key] = 'unknown';
      return acc;
    }, {}),
  );
  const [statusValidation, setStatusValidation] = useState<Record<string, ValidationStatus>>(
    () => ({
      passed: 'unknown',
      failed: 'unknown',
      errored: 'unknown',
      skipped: 'unknown',
      untested: 'unknown',
    }),
  );
  const [validatedCsvHeaders, setValidatedCsvHeaders] = useState<string[]>([]);
  const [validatedCsvStatuses, setValidatedCsvStatuses] = useState<string[]>([]);
  const [validatedFileName, setValidatedFileName] = useState<string | null>(null);
  const [validatedPreviewRows, setValidatedPreviewRows] = useState<string[][]>([]);
  const isDialogEmbedded = container === 'embedded';
  const isPanel = container === 'panel';
  const csvMappingQuery = useProjectCsvMappingQuery(projectId),
    updateCsvMapping = useUpdateProjectCsvMapping();
  const csvMappingError = useMutationErrorSnackbar();
  const isHeaderDirty = fieldOptions.some(
    (field) => mapping[field.key] !== draftMapping[field.key],
  );
  const isStatusDirty = Object.keys(statusAliases).some(
    (key) => statusAliases[key] !== draftStatusAliases[key],
  );
  const headerUpdateDisabled =
    !projectId || !isHeaderDirty || updateCsvMapping.isPending || csvMappingQuery.isPending;
  const statusUpdateDisabled =
    !projectId || !isStatusDirty || updateCsvMapping.isPending || csvMappingQuery.isPending;
  const clearDisabled =
    !Object.values(headerValidation).some((status) => status !== 'unknown') &&
    !Object.values(statusValidation).some((status) => status !== 'unknown');
  const validateInputRef = useRef<HTMLInputElement | null>(null);
  const resetValidationStates = useCallback(() => {
    setHeaderValidation((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        next[key] = 'unknown';
      });
      return next;
    });
    setStatusValidation((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        next[key] = 'unknown';
      });
      return next;
    });
  }, []);
  const clearValidation = useCallback(() => {
    resetValidationStates();
    setValidatedCsvHeaders([]);
    setValidatedCsvStatuses([]);
    setValidatedFileName(null);
    setValidatedPreviewRows([]);
  }, [resetValidationStates]);

  useEffect(() => {
    if (!isHashTargeted) return;
    const timer = window.setTimeout(() => {
      accordionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 150);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isHashTargeted]);

  useEffect(() => {
    if (!projectId) return;
    setMapping(defaultHeaderMapping);
    setDraftMapping(defaultHeaderMapping);
    setStatusAliases(defaultStatusMapping);
    setDraftStatusAliases(defaultStatusMapping);
    clearValidation();
  }, [clearValidation, defaultHeaderMapping, defaultStatusMapping, projectId]);

  useEffect(() => {
    if (!csvMappingQuery.data) return;
    const nextHeaderMapping = buildHeaderMapping(csvMappingQuery.data.headerAliases);
    const nextStatusMapping = buildStatusMapping(csvMappingQuery.data.statusAliases);
    setMapping(nextHeaderMapping);
    setDraftMapping(nextHeaderMapping);
    setStatusAliases(nextStatusMapping);
    setDraftStatusAliases(nextStatusMapping);
    clearValidation();
  }, [clearValidation, csvMappingQuery.data]);

  const normalizeHeader = useCallback((value: string): string => value.trim().toLowerCase(), []);
  const normalizeStatus = useCallback(
    (value: string): string =>
      value
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ''),
    [],
  );
  const parseAliasList = useCallback(
    (value: string): string[] =>
      value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    [],
  );
  const syncValidationWithDetected = useCallback(
    (nextDraftMapping: Record<string, string>, nextDraftStatusAliases: Record<string, string>) => {
      if (validatedCsvHeaders.length === 0) {
        resetValidationStates();
        return;
      }

      const headerSet = new Set(
        validatedCsvHeaders
          .map((header) => normalizeHeader(header))
          .filter((header) => header.length > 0),
      );
      const nextHeaderValidation: Record<string, ValidationStatus> = {};
      fieldOptions.forEach((field) => {
        const aliases = parseAliasList(nextDraftMapping[field.key]).map(normalizeHeader);
        if (aliases.length === 0) {
          nextHeaderValidation[field.key] = 'unknown';
          return;
        }
        nextHeaderValidation[field.key] = aliases.some((alias) => headerSet.has(alias))
          ? 'valid'
          : 'invalid';
      });
      setHeaderValidation(nextHeaderValidation);

      const statusSet = new Set(
        validatedCsvStatuses
          .map((statusValue) => normalizeStatus(statusValue))
          .filter((statusValue) => statusValue.length > 0),
      );
      const nextStatusValidation: Record<string, ValidationStatus> = { ...statusValidation };
      Object.keys(defaultCsvStatusAliases).forEach((key) => {
        const aliases = parseAliasList(nextDraftStatusAliases[key]).map(normalizeStatus);
        if (aliases.length === 0) {
          nextStatusValidation[key] = 'unknown';
          return;
        }
        nextStatusValidation[key] = aliases.some((alias) => statusSet.has(alias))
          ? 'valid'
          : 'invalid';
      });
      setStatusValidation(nextStatusValidation);
    },
    [
      fieldOptions,
      normalizeHeader,
      normalizeStatus,
      parseAliasList,
      resetValidationStates,
      statusValidation,
      validatedCsvHeaders,
      validatedCsvStatuses,
    ],
  );

  const resolveValidationIcon = useCallback((status: ValidationStatus): ReactElement => {
    if (status === 'valid') {
      return (
        <CursorTooltip
          title="Matched"
          placement="right-start"
          arrow
          slotProps={validationIconCursorTooltipSlotProps}
        >
          <CheckCircleOutlineIcon
            fontSize="small"
            sx={(theme) => ({ color: resolveSemanticColor(theme, 'success'), opacity: 0.82 })}
          />
        </CursorTooltip>
      );
    }
    if (status === 'invalid') {
      return (
        <CursorTooltip
          title="No match found."
          placement="right-start"
          arrow
          slotProps={validationIconCursorTooltipSlotProps}
        >
          <ErrorOutline
            fontSize="small"
            sx={(theme) => ({ color: resolveSemanticColor(theme, 'error') })}
          />
        </CursorTooltip>
      );
    }
    return (
      <CursorTooltip
        title="Not yet validated"
        placement="right-start"
        arrow
        slotProps={validationIconCursorTooltipSlotProps}
      >
        <HelpOutline
          fontSize="small"
          sx={(theme) => ({ color: resolveSemanticColor(theme, 'textSecondary'), opacity: 0.55 })}
        />
      </CursorTooltip>
    );
  }, []);

  const handleValidateFile = useCallback(
    async (file: File): Promise<void> => {
      const rows = await parseValidationRows(file);
      setValidatedFileName(file.name);
      setValidatedPreviewRows(rows);
      if (rows.length === 0) {
        setValidatedCsvHeaders([]);
        setValidatedCsvStatuses([]);
        setHeaderValidation((current) => {
          const next = { ...current };
          fieldOptions.forEach((field) => {
            const aliases = parseAliasList(draftMapping[field.key]);
            next[field.key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        setStatusValidation((current) => {
          const next = { ...current };
          Object.keys(defaultCsvStatusAliases).forEach((key) => {
            const aliases = parseAliasList(draftStatusAliases[key]);
            next[key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        return;
      }

      const headerRow = rows[0] ?? [];
      setValidatedCsvHeaders(
        headerRow.map((header) => header.trim()).filter((header) => header.length > 0),
      );
      const headers = headerRow.map((header) => normalizeHeader(header));
      const headerSet = new Set(headers.filter((header) => header.length > 0));

      const nextHeaderValidation: Record<string, ValidationStatus> = {};
      fieldOptions.forEach((field) => {
        const aliases = parseAliasList(draftMapping[field.key]).map(normalizeHeader);
        if (aliases.length === 0) {
          nextHeaderValidation[field.key] = 'unknown';
          return;
        }
        const matches = aliases.some((alias) => headerSet.has(alias));
        nextHeaderValidation[field.key] = matches ? 'valid' : 'invalid';
      });
      setHeaderValidation(nextHeaderValidation);

      const statusHeaderAliases = parseAliasList(draftMapping.status).map(normalizeHeader);
      const statusHeaderIndex = headers.findIndex((header) => statusHeaderAliases.includes(header));
      if (statusHeaderIndex === -1) {
        setValidatedCsvStatuses([]);
        setStatusValidation((current) => {
          const next = { ...current };
          Object.keys(defaultCsvStatusAliases).forEach((key) => {
            const aliases = parseAliasList(draftStatusAliases[key]);
            next[key] = aliases.length > 0 ? 'invalid' : 'unknown';
          });
          return next;
        });
        return;
      }

      const statusValues = new Set<string>();
      const statusValueByNormalized = new Map<string, string>();
      rows.slice(1, 201).forEach((row) => {
        if (statusHeaderIndex >= row.length) return;
        const rawValue = row[statusHeaderIndex];
        const trimmedValue = rawValue.trim();
        const normalizedValue = normalizeStatus(rawValue);
        if (normalizedValue.length > 0) {
          statusValues.add(normalizedValue);
          if (!statusValueByNormalized.has(normalizedValue) && trimmedValue.length > 0) {
            statusValueByNormalized.set(normalizedValue, trimmedValue);
          }
        }
      });
      setValidatedCsvStatuses(Array.from(statusValueByNormalized.values()));

      const nextStatusValidation: Record<string, ValidationStatus> = { ...statusValidation };
      Object.keys(defaultCsvStatusAliases).forEach((key) => {
        const aliases = parseAliasList(draftStatusAliases[key]).map(normalizeStatus);
        if (aliases.length === 0) {
          nextStatusValidation[key] = 'unknown';
          return;
        }
        const matches = aliases.some((alias) => statusValues.has(alias));
        nextStatusValidation[key] = matches ? 'valid' : 'invalid';
      });
      setStatusValidation(nextStatusValidation);
    },
    [
      draftMapping,
      draftStatusAliases,
      fieldOptions,
      normalizeHeader,
      normalizeStatus,
      parseValidationRows,
      parseAliasList,
      statusValidation,
    ],
  );
  const handleValidationFilesSelected = useCallback(
    (files: File[]): void => {
      const matchingFile = files.find((file) => /\.(csv|xlsx)$/i.test(file.name));
      if (!matchingFile) return;
      void handleValidateFile(matchingFile);
    },
    [handleValidateFile],
  );
  const previewHeaders = validatedPreviewRows[0] ?? [];
  const previewBodyRows = validatedPreviewRows.slice(1);
  const resolveMatchedHeaderLabel = useCallback(
    (header: string): string | null => {
      const normalizedHeader = normalizeHeader(header);
      const matchedField = fieldOptions.find((field) =>
        parseAliasList(draftMapping[field.key]).map(normalizeHeader).includes(normalizedHeader),
      );
      return matchedField?.label ?? null;
    },
    [draftMapping, normalizeHeader, parseAliasList],
  );
  const resolveMatchedStatusLabel = useCallback(
    (value: string, _columnIndex: number): string | null => {
      const normalizedValue = normalizeStatus(value);
      const matchedStatus = statusFieldDefinitions.find((field) =>
        parseAliasList(draftStatusAliases[field.key])
          .map(normalizeStatus)
          .includes(normalizedValue),
      );
      return matchedStatus?.label ?? null;
    },
    [draftStatusAliases, normalizeStatus, parseAliasList],
  );
  const previewData = useMemo(
    () =>
      buildValidationPreviewData({
        headers: previewHeaders,
        rows: previewBodyRows,
        headerDefinitions: fieldOptions,
        statusDefinitions: statusFieldDefinitions,
        headerValidation,
        statusValidation,
        draftMapping,
        draftStatusAliases,
        normalizeHeader,
        normalizeStatus,
        parseAliasList,
      }),
    [
      draftMapping,
      draftStatusAliases,
      headerValidation,
      normalizeHeader,
      normalizeStatus,
      parseAliasList,
      previewBodyRows,
      previewHeaders,
      statusValidation,
    ],
  );

  const validationInput = (
    <input
      id="project-csv-validation-input"
      ref={validateInputRef}
      type="file"
      hidden
      accept=".csv,.xlsx"
      onChange={(event) => {
        handleValidationFilesSelected(Array.from(event.target.files ?? []));
        event.target.value = '';
      }}
    />
  );

  const headerMappingFields = (
    <Stack gap="none">
      {fieldOptions.map((field) => (
        <ProjectCsvMappingFieldRow
          key={field.key}
          label={field.label}
          value={draftMapping[field.key]}
          placeholder="Enter header name"
          adornment={resolveValidationIcon(headerValidation[field.key] ?? 'unknown')}
          onChange={(nextValue) => {
            const nextDraftMapping = { ...draftMapping, [field.key]: nextValue };
            setDraftMapping(nextDraftMapping);
            syncValidationWithDetected(nextDraftMapping, draftStatusAliases);
          }}
        />
      ))}
    </Stack>
  );

  const statusMappingFields = (
    <Stack gap="none">
      {statusFieldDefinitions.map((field) => (
        <ProjectCsvMappingFieldRow
          key={field.key}
          label={field.label}
          value={draftStatusAliases[field.key]}
          placeholder="Enter status aliases"
          adornment={resolveValidationIcon(statusValidation[field.key])}
          onChange={(nextValue) => {
            const nextDraftStatusAliases = { ...draftStatusAliases, [field.key]: nextValue };
            setDraftStatusAliases(nextDraftStatusAliases);
            syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
          }}
        />
      ))}
    </Stack>
  );

  const saveCsvMapping = useCallback(
    (
      nextDraftMapping: Record<string, string>,
      nextDraftStatusAliases: Record<string, string>,
      successMessage: string,
    ): void => {
      if (!projectId) return;

      const suiteAliases = parseAliasList(nextDraftMapping.suite);
      const testAliases = parseAliasList(nextDraftMapping.test);
      const statusAliasesList = parseAliasList(nextDraftMapping.status);
      const durationAliases = parseAliasList(nextDraftMapping.duration);
      const timestampAliases = parseAliasList(nextDraftMapping.timestamp);
      const headerAliases: CsvHeaderAliases = {
        suite: suiteAliases,
        test: testAliases,
        status: statusAliasesList,
        ...(durationAliases.length > 0 ? { duration: durationAliases } : {}),
        ...(timestampAliases.length > 0 ? { timestamp: timestampAliases } : {}),
      };
      const nextStatusAliasesPayload: CsvStatusAliases = {
        passed: parseAliasList(nextDraftStatusAliases.passed),
        failed: parseAliasList(nextDraftStatusAliases.failed),
        errored: parseAliasList(nextDraftStatusAliases.errored),
        skipped: parseAliasList(nextDraftStatusAliases.skipped),
        untested: parseAliasList(nextDraftStatusAliases.untested),
      };

      updateCsvMapping.mutate(
        { projectId, headerAliases, statusAliases: nextStatusAliasesPayload },
        {
          onSuccess: (updated) => {
            const nextHeaderMapping = buildHeaderMapping(updated.headerAliases);
            const nextStatusMapping = buildStatusMapping(updated.statusAliases);
            setMapping(nextHeaderMapping);
            setDraftMapping(nextHeaderMapping);
            setStatusAliases(nextStatusMapping);
            setDraftStatusAliases(nextStatusMapping);
            syncValidationWithDetected(nextHeaderMapping, nextStatusMapping);
            csvMappingError.clearError();
            notify({ message: successMessage, tone: 'green' });
          },
          onError: (err) => {
            csvMappingError.setError(err);
          },
        },
      );
    },
    [
      csvMappingError,
      notify,
      parseAliasList,
      projectId,
      syncValidationWithDetected,
      updateCsvMapping,
    ],
  );
  const headerMappingActions = (
    <ProjectCsvMappingActions
      cancelDisabled={headerUpdateDisabled}
      updateDisabled={headerUpdateDisabled}
      onCancel={() => {
        const nextDraftMapping = { ...mapping };
        setDraftMapping(nextDraftMapping);
        syncValidationWithDetected(nextDraftMapping, draftStatusAliases);
        csvMappingError.clearError();
      }}
      onUpdate={() => {
        saveCsvMapping(
          draftMapping,
          draftStatusAliases,
          getToastMessage('project.headerMappingUpdated'),
        );
      }}
    />
  );
  const statusMappingActions = (
    <ProjectCsvMappingActions
      cancelDisabled={statusUpdateDisabled}
      updateDisabled={statusUpdateDisabled}
      onCancel={() => {
        const nextDraftStatusAliases = { ...statusAliases };
        setDraftStatusAliases(nextDraftStatusAliases);
        syncValidationWithDetected(draftMapping, nextDraftStatusAliases);
        csvMappingError.clearError();
      }}
      onUpdate={() => {
        saveCsvMapping(
          draftMapping,
          draftStatusAliases,
          getToastMessage('project.statusMappingUpdated'),
        );
      }}
    />
  );
  const headerMappingContent = (
    <ProjectCsvMappingPanelGroup
      actions={headerMappingActions}
      description="Column names accepted from uploaded files."
      isEmbedded={isDialogEmbedded}
      title="Header Mapping"
    >
      {headerMappingFields}
    </ProjectCsvMappingPanelGroup>
  );
  const statusMappingContent = (
    <ProjectCsvMappingPanelGroup
      actions={statusMappingActions}
      description="Result values are normalised before matching."
      isEmbedded={isDialogEmbedded}
      title="Status Mapping"
      withDivider
    >
      {statusMappingFields}
    </ProjectCsvMappingPanelGroup>
  );
  const resultsFileMappingPanel = isDialogEmbedded ? (
    <DialogSection align="stretch">
      {headerMappingContent}
      {statusMappingContent}
    </DialogSection>
  ) : (
    <Stack gap="xl">
      {headerMappingContent}
      {statusMappingContent}
    </Stack>
  );

  const validationPanel = (
    <ProjectCsvUploadValidationPanel
      validationInput={validationInput}
      clearDisabled={clearDisabled}
      clearValidation={clearValidation}
      validatedFileName={validatedFileName}
      openFilePicker={() => {
        validateInputRef.current?.click();
      }}
      hasValidatedFile={previewHeaders.length > 0}
      previewData={previewData}
      previewHeaders={previewHeaders}
      previewBodyRows={previewBodyRows}
      normalizeHeader={normalizeHeader}
      normalizeStatus={normalizeStatus}
      resolveMatchedHeaderLabel={resolveMatchedHeaderLabel}
      resolveMatchedStatusLabel={resolveMatchedStatusLabel}
    />
  );

  if (isDialogEmbedded) {
    return (
      <DialogBody align="stretch">
        <DialogSection align="stretch">{validationPanel}</DialogSection>
        {resultsFileMappingPanel}
      </DialogBody>
    );
  }

  if (isPanel) {
    return (
      <Panel
        title="XLSX / CSV Results File Mapping"
        surfaceVariant="settings"
        sx={settingsPanelSurfaceSx}
      >
        <Stack gap="xl">
          <Text role="body" color="textSecondary">
            Configure how uploaded results file fields map into FlowQA test data.
          </Text>
          {validationPanel}
          <Stack gap="xl">
            {headerMappingContent}
            {statusMappingContent}
          </Stack>
        </Stack>
      </Panel>
    );
  }

  const mappingContent = (
    <Stack gap={mappingPanelsGap}>
      <Box>{validationPanel}</Box>
      <Box sx={projectSettingsCsvMappingPanelDividerSx}>{headerMappingContent}</Box>
      {statusMappingContent}
    </Stack>
  );

  return (
    <ProjectSettingsAccordion
      id={projectCsvMappingAnchorId}
      rootRef={accordionRef}
      title="XLSX / CSV Results File Mapping"
      defaultExpanded={isHashTargeted}
      forceExpanded={isHashTargeted}
      preloadContent={isHashTargeted}
      transitionMode={isHashTargeted ? 'none' : 'auto'}
    >
      {mappingContent}
    </ProjectSettingsAccordion>
  );
};
